import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import { VaultService } from '../../services/vault.service';
import PasswordList from '../../components/vault/PasswordList';
import NotesList from '../../components/notes/NotesList';
import CardDetailsList from '../../components/cardDetails/CardDetailsList';
import PersonalDetailsList from '../../components/personalDetails/PersonalDetailsList';
import { IDecryptedPasswordEntry } from '../../types/vault.types';
import { Note } from '../../types/notes';
import { CardDetail } from '../../types/cardDetails';
import { PersonalDetail } from '../../types/personalDetails';
import { PlusIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { BankDetail } from '../../types/bankDetails';
import BankDetailsList from '../../components/bankDetails/BankDetailsList';
import CreateNoteModal from '../../components/notes/CreateNoteModal';
import BankDetailsModal from '../../components/bankDetails/BankDetailsModal';
import CardDetailsModal from '../../components/cardDetails/CardDetailsModal';
import PersonalDetailsModal from '../../components/personalDetails/PersonalDetailsModal';
import { useVault } from '../../contexts/VaultContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';

// Tab type definition
type TabType = 'passwords' | 'notes' | 'cardDetails' | 'bankDetails' | 'personalDetails';

const FavoritePage: React.FC = () => {
    const navigate = useNavigate();
    const { searchQuery } = useVault();
    const { activeWorkspace } = useWorkspace();

    // State variables for all items
    const [entries, setEntries] = useState<IDecryptedPasswordEntry[]>([]);
    const [notes, setNotes] = useState<Note[]>([]);
    const [cardDetails, setCardDetails] = useState<CardDetail[]>([]);
    const [personalDetails, setPersonalDetails] = useState<PersonalDetail[]>([]);
    const [bankDetails, setBankDetails] = useState<BankDetail[]>([]);

    // Loading state
    const [loading, setLoading] = useState<boolean>(true);

    // Active tab state
    const [activeTab, setActiveTab] = useState<TabType>('passwords');

    // Modal states
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState<Note | null>(null);
    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState<CardDetail | null>(null);
    const [isBankModalOpen, setIsBankModalOpen] = useState(false);
    const [selectedBank, setSelectedBank] = useState<BankDetail | null>(null);
    const [isPersonalModalOpen, setIsPersonalModalOpen] = useState(false);
    const [selectedPersonal, setSelectedPersonal] = useState<PersonalDetail | null>(null);

    // Fetch all favorites data at once
    const fetchData = async () => {
        setLoading(true);
        try {
            const workspaceId = activeWorkspace?.id;
            const allItemsData = await VaultService.getFavouriteItemsData(workspaceId);
            if (allItemsData) {
                setEntries(allItemsData?.passwords);
                setNotes(allItemsData?.notes);
                setCardDetails(allItemsData?.cards);
                setPersonalDetails(allItemsData?.personals);
                setBankDetails(allItemsData?.banks);
            }
        } catch (error) {
            console.error('Error fetching favorites:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Refresh data when active workspace changes
    useEffect(() => {
        if (activeWorkspace) {
            fetchData();
        }
    }, [activeWorkspace?.id]);

    // Filter items based on search query
    const filteredEntries = entries.filter(entry => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            entry.title?.toLowerCase().includes(query) ||
            entry.username?.toLowerCase().includes(query) ||
            entry.website_url?.toLowerCase().includes(query) ||
            entry.notes?.toLowerCase().includes(query)
        );
    });

    const filteredNotes = notes.filter(note => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            note.name?.toLowerCase().includes(query) ||
            note.encrypted_notes?.toLowerCase().includes(query)
        );
    });

    const filteredCardDetails = cardDetails.filter(card => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            card.name?.toLowerCase().includes(query) ||
            card.encrypted_card_holder_name?.toLowerCase().includes(query) ||
            card.card_type?.toLowerCase().includes(query)
        );
    });

    const filteredBankDetails = bankDetails.filter(bank => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            bank.name?.toLowerCase().includes(query) ||
            bank.encrypted_iban_number?.toLowerCase().includes(query) ||
            bank.encrypted_account_number?.toLowerCase().includes(query) ||
            bank.encrypted_routing_number?.toLowerCase().includes(query)
        );
    });

    const filteredPersonalDetails = personalDetails.filter(detail => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            detail.name?.toLowerCase().includes(query) ||
            detail.encrypted_first_name?.toLowerCase().includes(query) ||
            detail.encrypted_last_name?.toLowerCase().includes(query) ||
            detail.encrypted_email_address?.toLowerCase().includes(query)
        );
    });

    // Tab configuration
    const tabs = [
        { id: 'passwords', label: 'Passwords', count: filteredEntries.length },
        // { id: 'notes', label: 'Notes', count: filteredNotes.length },
        { id: 'cardDetails', label: 'Cards', count: filteredCardDetails.length },
        { id: 'bankDetails', label: 'Bank Accounts', count: filteredBankDetails.length },
        { id: 'personalDetails', label: 'Personal Info', count: filteredPersonalDetails.length },
    ];

    const handleEditNote = (note: Note) => {
        setSelectedNote(note);
        setIsNoteModalOpen(true);
    };

    const handleNoteModalClose = () => {
        setIsNoteModalOpen(false);
        setSelectedNote(null);
    };

    const handleCardModalClose = () => {
        setIsCardModalOpen(false);
        setSelectedCard(null);
    };

    const handleBankModalClose = () => {
        setIsBankModalOpen(false);
        setSelectedBank(null);
    };

    const handlePersonalModalClose = () => {
        setIsPersonalModalOpen(false);
        setSelectedPersonal(null);
    };

    const handleViewMore = (section: string) => {
        // Redirect to the corresponding detailed page
        if (section === 'passwords') {
            navigate('/passwords');
        } else if (section === 'notes') {
            navigate('/notes');
        } else if (section === 'cardDetails') {
            navigate('/card-details');
        } else if (section === 'personalDetails') {
            navigate('/personal-details');
        } else if (section === 'bankDetails') {
            navigate('/bank-details');
        }
    };

    return (
        <div className="h-screen flex overflow-hidden bg-gray-100">
            <VaultSidebar />
            <div className="flex flex-col flex-1 overflow-hidden">
                <VaultHeader />
                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                    {/* Hero Banner Section */}
                    <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
                            <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
                            <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
                        </div>
                        
                        <div className="relative px-6 py-12">
                            <div className="max-w-7xl mx-auto">
                                {/* Header Content */}
                                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                                    <div className="mb-6 lg:mb-0">
                                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4 font-sans-serif">
                                            Favorites
                                        </h1>
                                        <p className="text-xl text-white/90 max-w-2xl leading-relaxed font-sans">
                                            Quick access to your most important and frequently used items.
                                            Your favorite passwords, cards, bank details, and personal information all in one place.
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Stats Cards */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {filteredEntries.length}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Passwords
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {filteredCardDetails.length}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Cards
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {filteredBankDetails.length}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Banks
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {filteredPersonalDetails.length}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Personal
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Modern Tab Navigation */}
                                <div className="flex items-center justify-center px-4 sm:px-0">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-1 sm:p-2 border border-white/30 w-full sm:w-auto sm:max-w-none overflow-x-auto">
                                        <nav className="flex space-x-1 sm:space-x-2 min-w-max sm:min-w-0 sm:justify-center">
                                            {tabs.map((tab) => (
                                                <button
                                                    key={tab.id}
                                                    onClick={() => setActiveTab(tab.id as TabType)}
                                                    className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                                                        activeTab === tab.id
                                                            ? 'bg-white text-primary-600 shadow-lg scale-105'
                                                            : 'text-white/80 hover:text-white hover:bg-white/20'
                                                    }`}
                                                >
                                                    <div className="flex items-center space-x-1 sm:space-x-2">
                                                        {tab.id === 'passwords' && (
                                                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                            </svg>
                                                        )}
                                                        {tab.id === 'cardDetails' && (
                                                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                            </svg>
                                                        )}
                                                        {tab.id === 'bankDetails' && (
                                                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                            </svg>
                                                        )}
                                                        {tab.id === 'personalDetails' && (
                                                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                            </svg>
                                                        )}
                                                        <span>{tab.label}</span>
                                                    </div>
                                                    {activeTab === tab.id && (
                                                        <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-1 sm:border-2 border-white"></div>
                                                    )}
                                                </button>
                                            ))}
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="container mx-auto px-6 py-8">
                        {/* Tab Content */}
                        <div className="space-y-6">
                            {loading ? (
                                <div className="flex justify-center items-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                                </div>
                            ) : (
                                <>
                                    {/* Passwords Tab */}
                                    {activeTab === 'passwords' && (
                                        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                            <PasswordList
                                                entries={filteredEntries}
                                                onSuccessfulUpdate={fetchData}
                                                hideFavoriteIcon={true}
                                            />
                                        </div>
                                    )}

                                    {/* Cards Tab */}
                                    {activeTab === 'cardDetails' && (
                                        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                            <CardDetailsList
                                                details={filteredCardDetails}
                                                onUpdate={fetchData}
                                                onEdit={(card) => {
                                                    setSelectedCard(card);
                                                    setIsCardModalOpen(true);
                                                }}
                                                hideFavoriteIcon={true}
                                            />
                                        </div>
                                    )}

                                    {/* Bank Details Tab */}
                                    {activeTab === 'bankDetails' && (
                                        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                            <BankDetailsList
                                                details={filteredBankDetails}
                                                onUpdate={fetchData}
                                                onEdit={(bank) => {
                                                    setSelectedBank(bank);
                                                    setIsBankModalOpen(true);
                                                }}
                                                hideFavoriteIcon={true}
                                            />
                                        </div>
                                    )}

                                    {/* Personal Details Tab */}
                                    {activeTab === 'personalDetails' && (
                                        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                            <PersonalDetailsList
                                                details={filteredPersonalDetails}
                                                onUpdate={fetchData}
                                                onEdit={(personal) => {
                                                    setSelectedPersonal(personal);
                                                    setIsPersonalModalOpen(true);
                                                }}
                                                hideFavoriteIcon={true}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </main>
            </div>

            {/* Modals */}
            <CreateNoteModal
                isOpen={isNoteModalOpen}
                onClose={handleNoteModalClose}
                onSuccess={fetchData}
                note={selectedNote || undefined}
            />
            <BankDetailsModal
                isOpen={isBankModalOpen}
                onClose={handleBankModalClose}
                onSuccess={() => {
                    handleBankModalClose();
                }}
                bank={selectedBank || undefined}
            />
            <CardDetailsModal
                isOpen={isCardModalOpen}
                onClose={handleCardModalClose}
                onSuccess={() => {
                    handleCardModalClose();
                }}
                card={selectedCard || undefined}
            />
            <PersonalDetailsModal
                isOpen={isPersonalModalOpen}
                onClose={handlePersonalModalClose}
                onSuccess={() => {
                    handlePersonalModalClose();
                }}
                personal={selectedPersonal || undefined}
            />
        </div>
    );
};

export default FavoritePage;